import React, { useState, useEffect } from 'react';
import { useToast } from '../../../contexts/ToastContext';
import FormModal from '../../common/FormModal';
import ConfirmModal from '../../common/ConfirmModal';
import FormField from '../../common/FormField';
import FormGrid from '../../common/FormGrid';
import { FaSave, FaEdit, FaTrash, FaTimes, FaPlus, FaSearch, FaTag } from 'react-icons/fa';
import './CatalogoProveedores.css';
import { getApiUrl } from '../../../utils/api';

const CatalogoCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoriaAEliminar, setCategoriaAEliminar] = useState(null);
  const toast = useToast();

  // Estado del formulario
  const [formulario, setFormulario] = useState({
    nombre: ''
  });

  // Cargar categorías al montar
  useEffect(() => {
    cargarCategorias();
  }, []);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: 'getCategoriasIngredientes' })
      });
      const data = await response.json();
      if (data.success) {
        setCategorias(data.data || []);
      } else {
        toast.error(data.message || 'Error al cargar categorías');
      }
    } catch (error) {
      console.error('Error al cargar categorías:', error);
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar categorías basado en la búsqueda
  const categoriasFiltradas = categorias.filter(categoria => {
    if (!busqueda) return true;

    const textoBusqueda = busqueda.toLowerCase();
    return categoria.nombre?.toLowerCase().includes(textoBusqueda);
  });

  // Seleccionar categoría (solo para resaltar visualmente)
  const seleccionarCategoria = (categoria) => {
    setCategoriaSeleccionada(categoria);
  };

  // Nueva categoría
  const nuevaCategoria = () => {
    setCategoriaSeleccionada(null);
    setFormulario({
      nombre: ''
    });
    setShowModal(true);
  };

  // Editar categoría en modal
  const editarCategoria = (categoria) => {
    setCategoriaSeleccionada(categoria);
    setFormulario({
      nombre: categoria.nombre || ''
    });
    setShowModal(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setCategoriaSeleccionada(null);
    setFormulario({
      nombre: ''
    });
  };

  // Guardar categoría
  const guardarCategoria = async () => {
    if (!formulario.nombre.trim()) {
      toast.error('El nombre de la categoría es requerido');
      return;
    }

    try {
      setGuardando(true);

      const esEdicion = !!categoriaSeleccionada;
      const payload = {
        tipo: esEdicion ? 'actualizarCategoriaIngrediente' : 'crearCategoriaIngrediente',
        nombre: formulario.nombre
      };

      if (esEdicion) {
        payload.idCategoria = categoriaSeleccionada.idCategoria;
      }

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        toast.success(esEdicion ? 'Categoría actualizada correctamente' : 'Categoría creada correctamente');
        handleCloseModal();
        cargarCategorias();
      } else {
        toast.error(data.message || 'Error al guardar la categoría');
      }

    } catch (error) {
      console.error('Error al guardar:', error);
      toast.error('Error al guardar la categoría');
    } finally {
      setGuardando(false);
    }
  };

  // Confirmar eliminar categoría
  const confirmarEliminarCategoria = (categoria) => {
    setCategoriaAEliminar(categoria);
    setShowDeleteModal(true);
  };

  // Eliminar categoría
  const eliminarCategoria = async () => {
    if (!categoriaAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'eliminarCategoriaIngrediente',
          idCategoria: categoriaAEliminar.idCategoria
        })
      });
      const data = await response.json();

      if (data.success) {
        toast.success('Categoría eliminada correctamente');
        setCategoriaSeleccionada(null);
        handleCloseDeleteModal();
        cargarCategorias();
      } else {
        toast.error(data.message || 'Error al eliminar la categoría');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast.error('Error al eliminar la categoría');
    } finally {
      setGuardando(false);
    }
  };

  // Cerrar modal de confirmación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoriaAEliminar(null);
  };

  return (
    <div className="catalogo-proveedores-excel">
      <div className="excel-header">
        <div className="excel-header-controls">
          <div className="excel-search-container">
            <div className="excel-search-group">
              <FaSearch className="search-icon" />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar categorías..."
                className="excel-search-input"
                title="Buscar por nombre"
              />
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="excel-clear-search"
                  title="Limpiar búsqueda"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          <div className="excel-acciones">
            <button
              onClick={nuevaCategoria}
              className="btn-excel btn-nuevo"
              title="Nueva categoría"
            >
              <FaPlus /> Nueva Categoría
            </button>
          </div>
        </div>
      </div>

      {/* Tabla estilo Excel */}
      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th className="excel-th">Nombre</th>
              <th className="excel-th excel-th-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="2" className="excel-loading">Cargando categorías...</td>
              </tr>
            ) : categoriasFiltradas.length === 0 ? (
              <tr>
                <td colSpan="2" className="excel-empty">
                  {busqueda ? `No se encontraron categorías que coincidan con "${busqueda}"` : 'No hay categorías registradas'}
                </td>
              </tr>
            ) : (
              categoriasFiltradas.map(categoria => (
                <tr
                  key={categoria.idCategoria}
                  className={`excel-row ${
                    categoriaSeleccionada?.idCategoria === categoria.idCategoria ? 'excel-row-selected' : ''
                  }`}
                  onClick={() => seleccionarCategoria(categoria)}
                >
                  <td className="excel-cell excel-cell-nombre">{categoria.nombre}</td>
                  <td className="excel-cell excel-cell-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        editarCategoria(categoria);
                      }}
                      className="excel-btn-action excel-btn-edit"
                      title="Editar categoría"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmarEliminarCategoria(categoria);
                      }}
                      className="excel-btn-action excel-btn-delete"
                      title="Eliminar categoría"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer con información */}
      <div className="excel-footer">
        <div className="excel-info">
          <span>Total de categorías: <strong>{categoriasFiltradas.length}</strong></span>
          {busqueda && <span> de <strong>{categorias.length}</strong></span>}
        </div>
        <div className="excel-shortcuts">
          <span className="shortcut">F2: Nueva | Del: Eliminar | Ctrl+S: Guardar</span>
        </div>
      </div>

      {/* Modal de formulario */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={categoriaSeleccionada ? 'Editar Categoría' : 'Nueva Categoría'}
        icon={<FaTag />}
        onSave={guardarCategoria}
        saveText={guardando ? 'Guardando...' : 'Guardar'}
        saving={guardando}
        maxHeight="35vh"
      >
        <FormGrid columns={1} gap="12px">
          <FormField
            label="Nombre"
            value={formulario.nombre}
            onChange={(e) => setFormulario({...formulario, nombre: e.target.value})}
            placeholder="Nombre de la categoría"
            required
            icon={<FaTag />}
          />
        </FormGrid>
      </FormModal>

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarCategoria}
        title="Eliminar Categoría"
        message={`¿Estás seguro de que deseas eliminar la categoría "${categoriaAEliminar?.nombre}"?`}
        type="danger"
      />
    </div>
  );
};

export default CatalogoCategorias;